import { inject, Injectable } from '@angular/core';
import { WINDOW } from '@campus/browser';
import { ReplaySubject } from 'rxjs';

declare global {
  interface Window {
    MathJax: {
      loader?: {
        load?: string[];
      };
      tex?: {
        packages?: { [key: string]: string[] };
        macros?: { [key: string]: string };
        inlineMath?: string[][];
        displayMath?: string[][];
      };
      options: {
        ignoreHtmlClass: string;
        processHtmlClass: string;
      };
      typesetPromise?: (code?) => void;
      startup?: {
        promise: Promise<any>;
        document: {
          clear: () => void;
          updateDocument: () => void;
        };
      };
      tex2chtml?: (code: string, options?: any) => any;
    };
  }
}

export interface MathJaxConfig {
  source: string;
  integrety: string;
  id: string;
}

// Strip Froala's inline tags and decode the entities Froala/DomSanitizer emit, so MathJax
// sees clean LaTeX. Keep in sync with polpo-api/common/lib/mathjax-renderer.js normalize().
// &amp; runs last so e.g. &amp;lt; decodes to the literal &lt;, not <.
function normalizeLatex(s: string): string {
  return s
    .replace(/<[^>]+>/g, '') // strip HTML tags
    .replace(/&apos;/g, "'") // decode ' entity
    .replace(/&quot;/g, '"') // decode " entity
    .replace(/&#x([0-9a-fA-F]+);/g, (_, h) => String.fromCharCode(parseInt(h, 16))) // decode hex entity &#xHH;
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n))) // decode decimal entity &#NN;
    .replace(/&circ;/g, '^') // HTML entity ^
    .replace(/ˆ/g, '^') // Unicode ^ U+02C6
    .replace(/&lt;/g, '<') // decode < entity
    .replace(/&gt;/g, '>') // decode > entity
    .replace(/&nbsp;/g, ' ') // decode non-breaking space entity
    .replace(/ /g, ' ') // fold U+00A0 to a normal space
    .replace(/€|&euro;/g, '\\unicode{x20AC}') // euro (literal or entity) → glyph
    .replace(/&amp;/g, '&'); // decode & entity (last; see above)
}

@Injectable({
  providedIn: 'root',
})
export class MathJaxService {
  private initializePromise: Promise<void>;
  private ready$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private window = inject(WINDOW);

  private mathJax: MathJaxConfig = {
    source: 'https://cdnjs.cloudflare.com/ajax/libs/mathjax/3.2.2/es5/tex-chtml.min.js',
    integrety: 'sha512-T8xxpazDtODy3WOP/c6hvQI2O9UPdARlDWE0CvH1Cfqc0TXZF6GZcEKL7tIR8VbfS/7s/J6C+VOqrD6hIo++vQ==',
    id: 'MathJaxScript',
  };

  constructor() {
    this.registerMathJaxAsync(this.mathJax).then(() => {
      this.ready$.next(true);
    });
  }

  protected async registerMathJaxAsync(mathJax: MathJaxConfig): Promise<void> {
    if (document.getElementById(mathJax.id) || this.initializePromise) {
      return this.initializePromise;
    }
    this.window.MathJax = {
      // unicode extension: lets \unicode{...} render any code point (used for €).
      loader: { load: ['[tex]/unicode'] },
      tex: {
        packages: { '[+]': ['unicode'] },
        macros: { euro: '\\unicode{x20AC}' }, // \euro isn't built in; map it to the glyph
        inlineMath: [
          ['$', '$'],
          ['\\(', '\\)'],
        ],
        displayMath: [
          ['$$', '$$'],
          ['\\[', '\\]'],
        ],
      },
      options: {
        ignoreHtmlClass: 'no-mathjax',
        processHtmlClass: 'process-mathjax',
      },
    };

    this.initializePromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = mathJax.source;
      script.integrity = mathJax.integrety;
      script.id = mathJax.id;
      script.crossOrigin = 'anonymous';
      script.async = false;
      script.onload = () => {
        resolve();
      };
      script.onerror = () => {
        reject();
      };
      document.getElementsByTagName('head')[0].appendChild(script);
    });

    return this.initializePromise;
  }

  public ready() {
    return this.ready$;
  }

  public async render(input: string) {
    if (input === null || input === undefined) return '';

    if (!this.window.MathJax) {
      await this.registerMathJaxAsync(this.mathJax);
    }

    await this.window.MathJax.startup.promise;
    // Match each expression: \[…\] / $$…$$ (display), \(…\) / $…$ (inline). $$ precedes single
    // $ and the $ branch uses lookarounds so $$ isn't split into two $…$. Groups allow </> so
    // Froala-wrapped math matches; normalizeLatex() strips the tags. Mirrors the BE renderer.
    const regex = /\\\[([\s\S]+?)\\\]|\\\(([\s\S]+?)\\\)|\$\$([\s\S]+?)\$\$|(?<!\$)\$(?!\$)([\s\S]+?)(?<!\$)\$(?!\$)/g;
    const output = input.replace(regex, (_match, displayBackslash, inlineBackslash, displayDollar, inlineDollar) => {
      const isDisplay = displayBackslash !== undefined || displayDollar !== undefined;
      const rawLatex = displayBackslash ?? inlineBackslash ?? displayDollar ?? inlineDollar;
      const latex = normalizeLatex(rawLatex);
      // Guard per expression: a parse failure must not crash the whole render — fall back
      // to the raw input so the rest still previews.
      try {
        const html = this.window.MathJax.tex2chtml(latex, { display: isDisplay });
        return html.outerHTML;
      } catch (e) {
        console.warn('MathJax failed to render LaTeX:', rawLatex, e);
        return _match;
      }
    });

    this.window.MathJax.startup.document.clear();
    this.window.MathJax.startup.document.updateDocument();

    return output;
  }
}
